import { Component, ComponentType } from "@/types";
import { BaseCommandHandler, CommandResult, PropertyChange } from "../BaseCommandHandler";
import { createRangePatterns, createUnitPatterns, createThemePatterns, createColorPatterns } from "./ControlPatternMixins";

const VALID_THEMES = ['flat', 'gradient', 'neon-track', 'minimal', 'stepped'] as const;
type TickPlacement = 'outside' | 'inside' | 'none';

const THEME_ALIASES: Record<string, string> = {
  'neon': 'neon-track',
  'glow': 'neon-track',
  'segments': 'stepped',
  'step': 'stepped',
};

function readFiniteNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function readOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function isTickPlacement(value: unknown): value is TickPlacement {
  return value === 'outside' || value === 'inside' || value === 'none';
}

function readTickPlacementCommand(value: string): TickPlacement {
  return value.toLowerCase().startsWith('inside') ? 'inside' : 'outside';
}

export class SliderCommandHandler extends BaseCommandHandler {
  constructor() {
    super();
    this.initializePatterns();
  }

  private initializePatterns(): void {
    // Range patterns (min, max, step, value, range X to Y)
    for (const [regex, handler] of createRangePatterns()) {
      this.commandPatterns.set(regex, handler);
    }

    // Unit patterns
    for (const [regex, handler] of createUnitPatterns()) {
      this.commandPatterns.set(regex, handler);
    }

    // Theme patterns
    for (const [regex, handler] of createThemePatterns([...VALID_THEMES], THEME_ALIASES)) {
      this.commandPatterns.set(regex, handler);
    }

    // Color patterns
    for (const [regex, handler] of createColorPatterns(['trackColor', 'activeTrackColor', 'thumbColor'])) {
      this.commandPatterns.set(regex, handler);
    }

    // Orientation
    this.commandPatterns.set(
      /(?:make |set )?(?:orientation )?(horizontal|vertical)/i,
      (match) => ({ properties: { orientation: match[1].toLowerCase() } })
    );

    // Gauge-style vertical slider
    this.commandPatterns.set(
      /(?:make|use|set|switch to).*(?:vertical\s+gauge|gauge\s+slider|dimmer\s+gauge|gauge\s+style)/i,
      () => ({
        properties: {
          displayMode: 'vertical-gauge',
          orientation: 'vertical',
          showTicks: true,
        },
      })
    );

    // Show/hide value
    this.commandPatterns.set(
      /show value/i,
      () => ({ properties: { showValue: true } })
    );
    this.commandPatterns.set(
      /hide value/i,
      () => ({ properties: { showValue: false } })
    );

    // Show/hide ticks
    this.commandPatterns.set(
      /show ticks/i,
      () => ({ properties: { showTicks: true } })
    );
    this.commandPatterns.set(
      /hide ticks/i,
      () => ({ properties: { showTicks: false } })
    );

    // Tick placement
    this.commandPatterns.set(
      /(?:set |place |put |use )?(?:ticks?|tic(?:k)? marks?)\s+(?:on |to |at )?(inside|outside)/i,
      (match) => ({
        properties: {
          showTicks: true,
          tickPlacement: readTickPlacementCommand(match[1]),
        },
      })
    );
    this.commandPatterns.set(
      /(?:inside|outside)\s+(?:ticks?|tic(?:k)? marks?)/i,
      (match) => ({
        properties: {
          showTicks: true,
          tickPlacement: readTickPlacementCommand(match[0]),
        },
      })
    );
    this.commandPatterns.set(
      /(?:no|none|hide)\s+(?:ticks?|tic(?:k)? marks?)/i,
      () => ({ properties: { showTicks: false, tickPlacement: 'none' } })
    );

    // Show/hide min max
    this.commandPatterns.set(
      /show (?:min\s*\/?\s*max|labels|endpoints)/i,
      () => ({ properties: { showMinMax: true } })
    );
    this.commandPatterns.set(
      /hide (?:min\s*\/?\s*max|labels|endpoints)/i,
      () => ({ properties: { showMinMax: false } })
    );

    // Tick interval
    this.commandPatterns.set(
      /(?:set\s+)?tick(?:s)?\s+(?:(?:interval|every|at)\s*(?:to\s*)?)?(\d+(?:\.\d+)?)/i,
      (match) => ({ properties: { tickInterval: parseFloat(match[1]) } })
    );

    // Label
    this.commandPatterns.set(
      /(?:set )?label (?:to )?["']?(.+?)["']?\s*$/i,
      (match) => ({ properties: { label: match[1].trim() } })
    );

    // Decimals
    this.commandPatterns.set(
      /(?:set )?decimals? (?:to |= ?)?(\d+)/i,
      (match) => ({ properties: { decimals: parseInt(match[1], 10) } })
    );
  }

  canHandle(command: string, component: Component): boolean {
    return component.type === ComponentType.SLIDER && this.hasKeywords(command);
  }

  private hasKeywords(command: string): boolean {
    const keywords = [
      "slider", "range", "track", "thumb", "horizontal", "vertical",
      "tick", "ticks", "interval", "min", "max", "step", "value", "unit", "theme",
      "orientation", "label", "color", "decimals",
      "flat", "gradient", "neon-track", "neon", "minimal", "stepped",
    ];
    const lowerCommand = command.toLowerCase();
    return keywords.some(keyword => lowerCommand.includes(keyword));
  }

  async parseCommand(command: string, component: Component): Promise<CommandResult> {
    const commandParts = command.split(/\s+(?:and|with|also)\s+/i);
    const changes: PropertyChange = { properties: {} };

    for (const part of commandParts) {
      const trimmed = part.trim();
      if (!trimmed) continue;
      const patternMatch = this.matchPatterns(trimmed, component);
      if (patternMatch?.properties) {
        changes.properties = { ...changes.properties, ...patternMatch.properties };
      }
      if (patternMatch?.style) {
        changes.style = { ...changes.style, ...patternMatch.style };
      }
    }

    if (!changes.properties || Object.keys(changes.properties).length === 0) {
      return {
        success: false,
        error: "Could not understand slider command",
        suggestions: await this.getExampleCommands(ComponentType.SLIDER),
      };
    }

    const validation = this.validateChanges(changes, component);
    if (!validation.valid) {
      return { success: false, error: validation.error };
    }

    return {
      success: true,
      command: {
        type: this.determineCommandType(changes),
        changes,
        confidence: this.calculateConfidence(command, changes),
        description: this.generateDescription(changes, component),
      },
    };
  }

  protected override validateChanges(changes: PropertyChange, component: Component): { valid: boolean; error?: string } {
    const props = component.properties ?? {};
    const min = readFiniteNumber(changes.properties?.min) ?? readFiniteNumber(props.min) ?? 0;
    const max = readFiniteNumber(changes.properties?.max) ?? readFiniteNumber(props.max) ?? 100;
    const step = readFiniteNumber(changes.properties?.step) ?? readFiniteNumber(props.step) ?? 1;
    const decimals = readFiniteNumber(changes.properties?.decimals);
    const tickInterval = readFiniteNumber(changes.properties?.tickInterval);
    const tickPlacement = changes.properties?.tickPlacement;

    if (min >= max) {
      return { valid: false, error: "Min must be less than max" };
    }
    if (step <= 0) {
      return { valid: false, error: "Step must be greater than 0" };
    }
    if (decimals != null && decimals < 0) {
      return { valid: false, error: "Decimals must be 0 or greater" };
    }
    if (tickInterval != null && tickInterval <= 0) {
      return { valid: false, error: "Tick interval must be greater than 0" };
    }
    if (
      tickPlacement != null &&
      !isTickPlacement(tickPlacement)
    ) {
      return { valid: false, error: "Tick placement must be outside, inside, or none" };
    }

    return super.validateChanges(changes, component);
  }

  async getExampleCommands(_componentType: ComponentType): Promise<string[]> {
    return [
      "Set range 0 to 255",
      "Make vertical",
      "Show ticks",
      "Use gauge slider",
      "Inside ticks",
      "Theme gradient",
      "Track color blue",
      "Thumb color red",
      "Tick interval 10",
      "Unit percent",
    ];
  }

  async getSuggestions(component: Component): Promise<string[]> {
    const suggestions: string[] = [];
    const props = component.properties ?? {};
    const currentTheme = readOptionalString(props.theme) ?? 'flat';

    suggestions.push("Set range 0 to 255");
    suggestions.push("Make vertical");
    suggestions.push("Use gauge slider");
    suggestions.push("Show ticks");

    const otherThemes = VALID_THEMES.filter(t => t !== currentTheme);
    if (otherThemes.length > 0) {
      suggestions.push(`Theme ${otherThemes[0]}`);
    }

    suggestions.push("Track color blue");
    suggestions.push("Unit percent");

    return suggestions.slice(0, 6);
  }
}

export const sliderCommandHandler = new SliderCommandHandler();
